/**
 * C-Broker Search — the broker search consumer (ADR-047.1), tabbed like C-Search.
 *
 * The consumer reads `brokerpipe-updates.search` and folds each broker update into its own store, `broker_search`.
 * Three views: what it refused and why (ADR-047 §3.5), every brokerage it holds (sorted, filtered, paged), and a lookup by
 * MC. A brokerage opens on its own page (`consumer/BrokeragePage`). Read-only; times are the viewer's own time zone.
 */

import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import Table from '@mui/joy/Table'
import Tabs from '@mui/joy/Tabs'
import Typography from '@mui/joy/Typography'
import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { brokerPipelineApi } from 'api/brokerPipeline'
import { ErrorState, Loading, Stat, StatRow, WindowPicker, formatNumber, useLoadPipelineData } from '../load-pipeline/components/shared'
import { brokeragePath, when } from './components/shared'
import BrokeragesTab from './consumer/BrokeragesTab'

function Refusals() {
    const [hours, setHours] = React.useState(24)
    const { data, error, loading } = useLoadPipelineData(() => brokerPipelineApi.getConsumerRefusals(hours), [hours])
    const total = data?.totals.reduce((n, t) => n + t.count, 0) ?? 0

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <WindowPicker hours={hours} onChange={setHours} maxHours={168} />
            </Box>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 2 }}>
                An update the consumer cannot apply is recorded here and acknowledged: an unknown type, action or schema version, a broken contract, a
                non-register update for an MC its store does not hold, a DOT the register ties elsewhere, or a correction it cannot apply.
            </Typography>
            {error ? <ErrorState error={error} /> : null}
            {loading && !data ? <Loading /> : null}
            {data ? (
                <>
                    <StatRow>
                        <Stat label="Refused" value={formatNumber(total)} tone={total > 0 ? 'warning' : 'success'} />
                        {data.totals.map((t) => (
                            <Stat key={t.parse} label={t.parse} value={formatNumber(t.count)} />
                        ))}
                    </StatRow>
                    {data.sample.length > 0 ? (
                        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', mt: 2 }}>
                            <Table size="sm" stripe="odd">
                                <thead>
                                    <tr>
                                        <th>When</th>
                                        <th>Reason</th>
                                        <th>Source</th>
                                        <th>Message</th>
                                        <th>Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.sample.map((row) => (
                                        <tr key={row.id}>
                                            <td>{when(row.quarantinedAt)}</td>
                                            <td>{row.parse}</td>
                                            <td>{row.source ?? '—'}</td>
                                            <td>
                                                <code>{row.messageId ?? '—'}</code>
                                            </td>
                                            <td>{row.detail}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    ) : (
                        <Typography level="body-sm" sx={{ mt: 2 }}>
                            Nothing refused in this window.
                        </Typography>
                    )}
                </>
            ) : null}
        </>
    )
}

/** Looks one MC up and opens its page. */
function Inspector() {
    const navigate = useNavigate()
    const [draft, setDraft] = React.useState('')
    return (
        <Card variant="outlined" sx={{ maxWidth: 520 }}>
            <Typography level="title-sm">Open a brokerage by MC</Typography>
            <Box
                component="form"
                sx={{ display: 'flex', gap: 1 }}
                onSubmit={(e) => {
                    e.preventDefault()
                    if (draft.trim()) navigate(brokeragePath(draft.trim()))
                }}
            >
                <Input size="sm" placeholder="MC number" value={draft} onChange={(e) => setDraft(e.target.value)} sx={{ flex: 1 }} />
                <Button size="sm" type="submit">
                    Open
                </Button>
            </Box>
        </Card>
    )
}

const TABS = [
    { key: 'refusals', label: 'Consumer refusals', render: () => <Refusals /> },
    { key: 'brokerages', label: 'Brokerages', render: () => <BrokeragesTab /> },
    { key: 'inspector', label: 'Open by MC', render: () => <Inspector /> },
] as const

export default function BrokerPipelineConsumerPage() {
    const { tab } = useParams<{ tab?: string }>()
    const navigate = useNavigate()
    const active = TABS.some((t) => t.key === tab) ? (tab as string) : TABS[0].key

    return (
        <Box sx={{ p: 3 }}>
            <Typography level="h2" sx={{ mb: 0.5 }}>
                C-Broker Search
            </Typography>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 2 }}>
                Reads <code>brokerpipe-updates.search</code> and folds each update into its own store, <code>broker_search</code> (ADR-047.1). It runs only
                where <code>BROKER_SEARCH_CONSUMER_ENABLED</code> is on.
            </Typography>

            <Tabs value={active} onChange={(_, value) => navigate(`/broker-pipeline/consumer/${value}`)} sx={{ bgcolor: 'transparent' }}>
                <TabList tabFlex="none" sx={{ mb: 1 }}>
                    {TABS.map((t) => (
                        <Tab key={t.key} value={t.key} variant="plain" color="neutral">
                            {t.label}
                        </Tab>
                    ))}
                </TabList>
            </Tabs>

            {TABS.find((t) => t.key === active)?.render()}
        </Box>
    )
}
